import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { trackerKeys } from './hooks';

/**
 * The Research and draft queue.
 *
 * One button, on a prospect or on a whole batch. The row it writes is drained
 * overnight; nothing here talks to Senku directly.
 *
 * The labels are pure functions on purpose. They carry the only two things a
 * person reads off this feature — what it is about to do, and what it is waiting
 * on — and both are worth testing without a database.
 */

export type QueueState = 'queued' | 'sent' | 'done' | 'failed';

export interface ResearchRequest {
  id: string;
  prospect_id: string;
  state: QueueState;
  requested_at: string;
  sent_on: string | null;
  completed_at: string | null;
  failure_reason: string | null;
}

export interface ResearchPlan {
  /** Everyone in the batch. */
  total: number;
  /** Those with no research inside the staleness window and nothing already open. */
  due: number;
}

export const researchKeys = {
  all: () => [...trackerKeys.all, 'research-queue'] as const,
  plan: (batchId: string) => [...trackerKeys.all, 'research-plan', batchId] as const,
};

/**
 * What the batch button says before it is pressed.
 *
 * The 90-day rule is invisible, so the button has to state what it is about to
 * do. Press the same batch three months later and it reads the full count again.
 */
export function batchButtonLabel(plan: ResearchPlan | undefined): string {
  if (plan === undefined) return 'Research and draft';
  if (plan.total === 0) return 'Nobody in this batch';
  if (plan.due === 0) return `All ${plan.total} researched recently`;
  if (plan.due === plan.total) return `Research and draft ${plan.total}`;
  return `Research and draft ${plan.due} of ${plan.total}`;
}

/** Why the button is off, in words, or '' when it is on. */
export function batchButtonReason(plan: ResearchPlan | undefined): string {
  if (plan === undefined) return '';
  if (plan.total === 0) return 'This batch has no prospects yet.';
  if (plan.due === 0) {
    return `Everyone here was researched in the last 90 days. Re-run one by hand from their drawer if you need them refreshed.`;
  }
  const skipped = plan.total - plan.due;
  return skipped === 0 ? '' : `${skipped} skipped, researched in the last 90 days.`;
}

/**
 * What one prospect's row says.
 *
 * Deliberately says what happens next rather than naming the state. "queued"
 * tells somebody nothing; "back tomorrow morning" answers the question they
 * actually have.
 */
export function requestStatusLabel(request: ResearchRequest | undefined): string {
  if (request === undefined) return '';
  switch (request.state) {
    case 'queued':
      return 'Queued. Back tomorrow morning.';
    case 'sent':
      return 'With Senku tonight.';
    case 'done':
      // The answer is in the timeline by now, which is where somebody would
      // look for it. Saying "done" here would just be noise above it.
      return '';
    case 'failed':
      return `Did not come back: ${request.failure_reason ?? 'no reason recorded'}`;
  }
}

/** Whether the button should be offered at all for this prospect. */
export function canQueue(request: ResearchRequest | undefined): boolean {
  return request === undefined || request.state === 'done' || request.state === 'failed';
}

/** The newest request per prospect. The queue keeps history; the row shows now. */
export function latestByProspect(rows: readonly ResearchRequest[]): Map<string, ResearchRequest> {
  const newest = new Map<string, ResearchRequest>();
  for (const row of rows) {
    const seen = newest.get(row.prospect_id);
    if (seen === undefined || row.requested_at > seen.requested_at) {
      newest.set(row.prospect_id, row);
    }
  }
  return newest;
}

function fail(message: string): never {
  throw new Error(message);
}

export const RESEARCH_PAGE = 1000;
const RESEARCH_PAGE_LIMIT = 100;

/**
 * Every research request, paged.
 *
 * This was one request, and Supabase clamps every response to 1000 rows
 * without an error (Drop 230 found the same thing on prospects). With 1,900
 * prospects the queue passes 1000 soon, and the status strip counts from this
 * list, so a silent cut would read as research that never happened. Advances
 * by what each page returned, stops on the first empty page, fails loudly past
 * the limit. The id order breaks ties so no row repeats or goes missing at a
 * page boundary.
 */
export async function fetchResearchQueue(client: SupabaseClient): Promise<ResearchRequest[]> {
  const everyone: ResearchRequest[] = [];
  for (let page = 0; page < RESEARCH_PAGE_LIMIT; page += 1) {
    const from = everyone.length;
    const { data, error } = await client
      .from('prospect_research_queue')
      .select('id, prospect_id, state, requested_at, sent_on, completed_at, failure_reason')
      .order('requested_at', { ascending: false })
      .order('id', { ascending: true })
      .range(from, from + RESEARCH_PAGE - 1);
    if (error) fail(error.message);
    const rows = Array.isArray(data) ? (data as ResearchRequest[]) : [];
    if (rows.length === 0) return everyone;
    everyone.push(...rows);
  }
  fail(
    `More than ${String(RESEARCH_PAGE * RESEARCH_PAGE_LIMIT)} research requests; paging stopped.`,
  );
}

export function useResearchQueue() {
  const { client } = useAuth();
  return useQuery({
    queryKey: researchKeys.all(),
    queryFn: () => fetchResearchQueue(client),
  });
}

export function useResearchPlan(batchId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: researchKeys.plan(batchId ?? 'none'),
    enabled: batchId !== null,
    queryFn: async (): Promise<ResearchPlan> => {
      const { data, error } = await client.rpc('prospect_research_plan', {
        p_batch: batchId ?? '',
      });
      if (error) fail(error.message);
      const row = (data ?? [])[0];
      return { total: row?.total ?? 0, due: row?.due ?? 0 };
    },
  });
}

export function useQueueProspectResearch() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (prospectId: string): Promise<void> => {
      const { error } = await client.rpc('queue_prospect_research', { p_prospect: prospectId });
      if (error) fail(error.message);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
    },
  });
}

export function useQueueBatchResearch() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (batchId: string): Promise<number> => {
      const { data, error } = await client.rpc('queue_batch_research', { p_batch: batchId });
      if (error) fail(error.message);
      return data ?? 0;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
    },
  });
}

/** What to say after the batch button is pressed. */
export function queuedConfirmation(added: number): string {
  if (added === 0) return 'Nothing new to queue.';
  if (added === 1) return '1 prospect queued. Senku runs tonight, results in the morning.';
  return `${added} prospects queued. Senku runs tonight, results over the next few mornings.`;
}
